import React, { createContext, useContext } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useDjAuth } from './DjAuthContext';
import { Security } from '@okta/okta-react';
import { DjAuthProvider } from './DjAuthContext';
import OktaAuth from '@okta/okta-auth-js';

export type UnifiedAuthState = {
  isAuthenticated: boolean;
  authProvider: 'okta' | 'dj' | null;
};

export interface UnifiedAuthContextType {
  unifiedAuthState: UnifiedAuthState | null;
  logout: () => void;
}

export const AuthContext = createContext<UnifiedAuthContextType | undefined>(undefined);

// Handles provider wrapping and props
export const UnifiedAuthProvider: React.FC<{
  children: React.ReactNode;
  oktaAuth: OktaAuth;
  restoreOriginalUri: (oktaAuth: OktaAuth, originalUri: string) => void;
}> = ({ children, oktaAuth, restoreOriginalUri }) => {
  return (
    <DjAuthProvider>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <UnifiedAuthProviderInner>{children}</UnifiedAuthProviderInner>
      </Security>
    </DjAuthProvider>
  );
};

// Safely uses provider hooks from inside provider
export const UnifiedAuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState: oktaAuthState, oktaAuth } = useOktaAuth();
  const { djAuth, djAuthState } = useDjAuth();

  const unifiedAuthState = (() => {
    if (oktaAuthState === null || djAuthState === null) {
      return null;
    }

    if (oktaAuthState.isAuthenticated) {
      return {
        isAuthenticated: true,
        authProvider: 'okta' as const,
      };
    }

    if (djAuthState.isAuthenticated) {
      return {
        isAuthenticated: true,
        authProvider: 'dj' as const,
      };
    }

    return {
      isAuthenticated: false,
      authProvider: null,
    };
  })();

  const logout = () => {
    if (unifiedAuthState?.authProvider === 'okta') {
      oktaAuth.signOut();
    } else if (unifiedAuthState?.authProvider === 'dj') {
      djAuth.logout();
    }
  };

  return <AuthContext.Provider value={{ unifiedAuthState, logout }}>{children}</AuthContext.Provider>;
};

export const useUnifiedAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  return context;
};
